import React, { useState, useContext } from "react";
import {
	Card,
	Tag,
	Typography,
	List,
	Row,
	Col,
	Space,
	Divider,
	Modal,
	Statistic,
} from "antd";
import {
	CheckCircleOutlined,
	ArrowUpOutlined,
	ArrowDownOutlined,
	ClockCircleOutlined,
} from "@ant-design/icons";
import { sellBoostFetch } from "../../../utlifyFunction";
import PrimaryButton from "../../../components/Buttons/PrimaryButton";
import { DataContext } from "../../../contexts/DataContext";

const { Title, Text } = Typography;

export default function BillingTab() {
	const [selectedPlan, setSelectedPlan] = useState(null);
	const [isModalVisible, setIsModalVisible] = useState(false);
	const { creditHandlers } = useContext(DataContext);
	const { creditData, packagesData } = creditHandlers;

	const showModal = () => {
		setIsModalVisible(true);
	};

	const handleCancel = () => {
		setIsModalVisible(false);
	};

	const handleUpgrade = async () => {
		try {
			const response = await sellBoostFetch("POST", "/credits/purchase", {
				packageId: selectedPlan?._id,
			});

			if (response?.data?.confirmationUrl) {
				window.location.href = response.data.confirmationUrl;
			}
		} catch (error) {
			console.log(error);
		}
	};

	return (
		<div style={{ margin: "0 auto" }}>
			<Title
				level={2}
				style={{ color: "#222", fontWeight: "600", textAlign: "center" }}
			>
				🚀 SellBoost AI Credits
			</Title>

			{/* Credit Summary Cards */}
			<Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
				<Col xs={24} sm={12} md={8}>
					<Card>
						<Statistic
							title="Total Credits"
							value={creditData.total}
							prefix={<ArrowUpOutlined />}
							valueStyle={{ color: "#3f8600" }}
						/>
						<Text type="secondary">Lifetime credits acquired</Text>
					</Card>
				</Col>
				<Col xs={24} sm={12} md={8}>
					<Card>
						<Statistic
							title="Used Credits"
							value={creditData.used}
							prefix={<ArrowDownOutlined />}
							valueStyle={{ color: "#cf1322" }}
						/>
						<Text type="secondary">Credits spent on generations</Text>
					</Card>
				</Col>
				<Col xs={24} sm={12} md={8}>
					<Card>
						<Statistic
							title="Available Credits"
							value={creditData.available}
							valueStyle={{ color: "#1890ff", fontWeight: "bold" }}
						/>
						<Text type="secondary">Ready to use for AI generations</Text>
					</Card>
				</Col>
			</Row>

			<Row gutter={[24, 24]}>
				<Col span={24} md={16}>
					<Card
						title={
							<Title level={4} style={{ margin: 0 }}>
								📝 Recent Transactions
							</Title>
						}
						style={{ marginBottom: 24 }}
					>
						<List
							itemLayout="vertical"
							dataSource={creditData.recentTransactions}
							renderItem={(transaction) => (
								<List.Item
									key={transaction._id}
									extra={
										<Tag
											color={transaction.success ? "green" : "orange"}
											style={{ fontSize: 12 }}
										>
											{transaction.success ? (
												<Space>
													<CheckCircleOutlined /> Completed
												</Space>
											) : (
												<Space>
													<ClockCircleOutlined /> Pending
												</Space>
											)}
										</Tag>
									}
								>
									<List.Item.Meta
										title={
											<Space>
												<Text strong>{transaction.description}</Text>
												<Tag
													color={
														transaction.type === "credit" ? "green" : "volcano"
													}
												>
													{transaction.type === "credit" ? "+" : "-"}
													{transaction.amount} credits
												</Tag>
											</Space>
										}
										description={
											<Space direction="vertical" size={0}>
												<Text type="secondary">
													Transaction ID: {transaction.chargeId}
												</Text>
												<Text type="secondary">
													{new Date(transaction.createdAt).toLocaleString()}
												</Text>
												<Text>New balance: {transaction.balance} credits</Text>
											</Space>
										}
									/>
								</List.Item>
							)}
						/>
					</Card>
				</Col>

				<Col span={24} md={8}>
					<Card
						title={
							<Title level={4} style={{ margin: 0 }}>
								💳 Purchase Credits
							</Title>
						}
					>
						<Space direction="vertical" size={16} style={{ width: "100%" }}>
							<Text>
								Choose a credit pack to continue generating product
								descriptions:
							</Text>

							{packagesData?.map((plan) => (
								<Card
									key={plan._id}
									hoverable
									onClick={() => setSelectedPlan(plan)}
									style={{
										border:
											selectedPlan?._id === plan._id
												? "2px solid #1890ff"
												: "1px solid #f0f0f0",
										borderRadius: 8,
										cursor: "pointer",
									}}
								>
									<Space
										direction="vertical"
										size={4}
										style={{ width: "100%" }}
									>
										<Text strong>{plan.name}</Text>
										<Text>
											{plan.credits} credits for ${plan.price}
										</Text>
										<Text type="secondary" style={{ fontSize: 12 }}>
											{plan.description}
										</Text>
									</Space>
								</Card>
							))}

							<PrimaryButton onClick={showModal} disabled={!selectedPlan} block>
								{selectedPlan
									? `Purchase ${selectedPlan.name}`
									: "Select a Plan"}
							</PrimaryButton>
						</Space>
					</Card>
				</Col>
			</Row>

			{/* Purchase Confirmation Modal */}
			<Modal
				title={`Confirm Purchase: ${selectedPlan?.name}`}
				visible={isModalVisible}
				onOk={handleUpgrade}
				onCancel={handleCancel}
				okText="Confirm Purchase"
				cancelText="Cancel"
				width={600}
			>
				{selectedPlan && (
					<Space direction="vertical" size={16} style={{ width: "100%" }}>
						<Card bordered={false}>
							<Row gutter={24}>
								<Col span={12}>
									<Statistic title="Credit Pack" value={selectedPlan.name} />
								</Col>
								<Col span={12}>
									<Statistic title="Credits" value={selectedPlan.credits} />
								</Col>
							</Row>
							<Row gutter={24} style={{ marginTop: 16 }}>
								<Col span={12}>
									<Statistic title="Price" value={`$${selectedPlan.price}`} />
								</Col>
								<Col span={12}>
									<Statistic
										title="Price per credit"
										value={`$${(
											selectedPlan.price / selectedPlan.credits
										).toFixed(3)}`}
									/>
								</Col>
							</Row>
						</Card>

						<Divider />

						<Text strong>After purchase, you'll have:</Text>
						<Text>
							{creditData.available.toLocaleString()} (current) +{" "}
							{selectedPlan.credits.toLocaleString()} (new) ={" "}
							<Text strong>
								{(creditData.available + selectedPlan.credits).toLocaleString()}
							</Text>{" "}
							total credits
						</Text>

						<Divider />

						<Text type="secondary">
							You'll be redirected to Shopify to complete your payment. Your
							credits will be added immediately after successful payment.
						</Text>
					</Space>
				)}
			</Modal>
		</div>
	);
}
